import ServiceRecordAttribution from '../ServiceRecordAttribution';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ServicePhotoThumb from 'pages/MaintenancePage/components/LogServicePanel/components/ServicePhotosField/components/ServicePhotoThumb';
import { getServiceRecordSummary } from 'pages/MaintenancePage/helpers/getServiceRecordSummary';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import { PHOTOS_TEST_ID, TEST_ID, VOID_TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ServiceHistoryRow: FunctionComponent<Props> = ({
    className = '',
    isAdmin,
    onSelectRecord,
    onVoidRecord,
    record,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const summary = getServiceRecordSummary({ record, strings: t });
    // Discriminant narrowing: only non-calibration records carry a part serial.
    const partRecord = record.eventType === 'calibrated' ? undefined : record;
    const voidedClass = record.voided ? styles.isVoided : '';
    const showVoidAction = !record.voided && isAdmin;
    // Photos stay visible on voided records — the struck-through summary carries the signal.
    const photos = record.photos ?? [];

    const handleSelect = () => onSelectRecord({ recordId: record.id });

    // The void div stops the bubble itself so voiding never also opens the detail.
    const handleVoid = (clickEvent: { stopPropagation: VoidFunction }) => {
        clickEvent.stopPropagation();
        onVoidRecord({ recordId: record.id });
    };

    return (
        <VerticalContainer
            className={`${styles.serviceHistoryRow} ${voidedClass} ${className}`}
            onClick={handleSelect}
            testId={testId}>
            <Wrapper className={styles.summary}>{summary}</Wrapper>

            <Wrapper className={styles.date}>
                {new Date(record.performedAt).toLocaleDateString()}
            </Wrapper>

            {partRecord?.partSerialNumber && (
                <Wrapper className={styles.partSerialNumber}>
                    {t.SERVICE_RECORD_SERIAL_NUMBER({
                        serialNumber: partRecord.partSerialNumber
                    })}
                </Wrapper>
            )}

            {record.notes && <Wrapper className={styles.notes}>{record.notes}</Wrapper>}

            {photos.length > 0 && (
                <HorizontalContainer className={styles.photos} testId={PHOTOS_TEST_ID}>
                    {photos.map(photo => (
                        <ServicePhotoThumb key={photo.id} photo={photo} />
                    ))}
                </HorizontalContainer>
            )}

            <ServiceRecordAttribution record={record} />

            {showVoidAction && (
                <div className={styles.voidAction} data-testid={VOID_TEST_ID} onClick={handleVoid}>
                    {t.SERVICE_VOID}
                </div>
            )}
        </VerticalContainer>
    );
};

export default ServiceHistoryRow;
